import React, { useEffect, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';

import styles from './HealthStatusRows.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';
import { general } from '../../../general';

// Health values registered by the breed council (epilepsi, patella, LTV ...),
// with «- / Mistenkt / Diagnostisert» toggles. Which values a dog has is
// decided by the backend from the dog's breed (supportmodules/healthstatus.js).
// Renders table rows, so it goes inside the caller's <tbody>.
const HEALTH_STATUSES = ["mistenkt", "diagnostisert"];

class PendingChange {
    key!: string;
    status!: string | null;
}

class HealthStatus {
    keys: string[] = [];
    values: { [key: string]: string } = {};
    // When each value was last set (ISO string). Missing for values set
    // before the backend recorded it.
    changed?: { [key: string]: string } = {};
}

function HealthStatusRows(props: HealthStatusRowsProps) {
    const t = useTranslate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    const [healthStatus, setHealthStatus] = useState<HealthStatus>();
    const [savingKey, setSavingKey] = useState<string>();
    // A click only asks; the change is saved when the user confirms it
    const [pendingChange, setPendingChange] = useState<PendingChange>();
    // The toggle group of each value, so the confirmation opens next to it
    const toggleRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const canEdit = general.getBreedAccess(auth.user, auth.selectedBreed) == "admin";

    function getHealthStatus() {
        axios.single({
            method: 'get',
            url: "/api/dogs/" + props.dogId + "/healthstatus",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            // The backend answers [] when the user has no access to the dog
            setHealthStatus(Array.isArray(res.data?.keys) ? res.data : undefined);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function saveHealthStatus(key: string, status: string | null) {
        setSavingKey(key);
        axios.single({
            method: 'post',
            url: "/api/dogs/" + props.dogId + "/healthstatus",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: { key: key, status: status }
        }).then((res) => {
            if (Array.isArray(res.data?.keys)) setHealthStatus(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setSavingKey(undefined);
            setPendingChange(undefined);
        });
    }

    function statusLabel(status: string | null) {
        return t("dog.details.health.status.options." + (status ?? "none"));
    }

    function drawConfirm() {
        return (
            <Overlay
                target={pendingChange ? toggleRefs.current[pendingChange.key] : null}
                show={pendingChange != undefined}
                placement="bottom-end"
                rootClose
                onHide={(e: Event) => {
                    // A click on another option picks that one instead of closing
                    const onToggle = Object.values(toggleRefs.current).some((element) => element?.contains(e?.target as Node));
                    if (savingKey == undefined && !onToggle) setPendingChange(undefined);
                }}
            >
                <Popover className={styles.confirm}>
                    {pendingChange != undefined ?
                        <Popover.Body className={styles.confirmbody}>
                            <div className={styles.confirmtext}>
                                {t("dog.details.health.status.confirm.text", {
                                    name: t("dog.details.health.status." + pendingChange.key),
                                    to: statusLabel(pendingChange.status),
                                })}
                            </div>
                            <div className={styles.confirmactions}>
                                <button
                                    type="button"
                                    className={styles.confirmcancel}
                                    onClick={() => setPendingChange(undefined)}
                                    disabled={savingKey != undefined}
                                >
                                    {t("dog.details.health.status.confirm.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className={styles.confirmok}
                                    onClick={() => saveHealthStatus(pendingChange.key, pendingChange.status)}
                                    disabled={savingKey != undefined}
                                >
                                    {savingKey != undefined ? t("dog.details.health.status.confirm.saving") : t("dog.details.health.status.confirm.confirm")}
                                </button>
                            </div>
                        </Popover.Body>
                    : <></>}
                </Popover>
            </Overlay>
        );
    }

    function drawToggle(key: string) {
        const current = healthStatus?.values[key] ?? null;
        const options: (string | null)[] = [null, ...HEALTH_STATUSES];
        return (
            <div
                ref={(element) => { toggleRefs.current[key] = element; }}
                className={[styles.healthstatus, pendingChange?.key == key ? styles.pending : ""].join(" ")}
                role="group"
                aria-label={t("dog.details.health.status." + key)}
            >
                {options.map((option) => (
                    <button
                        key={option ?? "none"}
                        type="button"
                        data-label={statusLabel(option)}
                        className={[
                            styles.healthstatusoption,
                            current == option ? styles["selected_" + (option ?? "none")] : "",
                            pendingChange?.key == key && pendingChange.status == option ? styles.proposed : ""
                        ].join(" ")}
                        aria-pressed={current == option}
                        disabled={!canEdit || savingKey == key}
                        onClick={() => {
                            if (current != option) setPendingChange({ key: key, status: option });
                        }}
                    >
                        {statusLabel(option)}
                    </button>
                ))}
            </div>
        );
    }

    useEffect(() => {
        setHealthStatus(undefined);
        setPendingChange(undefined);
        if (props.dogId != undefined && auth.selectedBreed != undefined) {
            getHealthStatus();
        }
    }, [props.dogId]);

    return (
        <>
            { healthStatus?.keys.map((key) => (
                <tr key={key}>
                    <td><strong>{t("dog.details.health.status." + key)}:</strong></td>
                    <td>
                        <div className={styles.statuscell}>
                            {healthStatus.values[key] != undefined && healthStatus.changed?.[key] != undefined ?
                                <span className={styles.changed}>
                                    {t("dog.details.health.status.changed")} {general.dateToString(new Date(healthStatus.changed[key]))}
                                </span>
                            : null}
                            {drawToggle(key)}
                        </div>
                    </td>
                </tr>
            )) }
            {drawConfirm()}
        </>
    );
}

class HealthStatusRowsProps {
    dogId!: string | number | undefined;
}

export default HealthStatusRows;
